import { useNavigate } from '@tanstack/react-router';
import { Laptop, MessageSquare } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ChatHeader } from '../components/ChatHeader';
import { EmptyState } from '../components/EmptyState';
import { MacPermissionPicker } from '../components/MacPermissionPicker';
import { useAuth } from '../lib/auth';
import { chatDraftKey, getDraftEpoch } from '../lib/chatDrafts';
import { cn } from '../lib/cn';
import { DEFAULT_MAC_PERMISSION, type MacPermission } from '../lib/mac';
import { setPendingSend } from '../lib/pendingSend';
import { useCreateSession } from '../lib/sessions';
import type { Effort, Model, PermissionMode } from '../lib/settings';

/**
 * /new. Renders EmptyState (sparkle + greeting + pills + composer).
 * On submit: creates a session, stashes the text + chosen model
 * + permission mode in a module-level map keyed by the new session
 * id, then navigates to /chat/$id. The ChatPage that mounts on
 * the new URL drains the pending payload and fires it over the
 * WebSocket.
 */
export function NewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const createSession = useCreateSession();
  const macAvailable = user?.mac_available === true;
  const [macChosen, setMacChosen] = useState(false);
  const [macPermission, setMacPermission] = useState<MacPermission>(DEFAULT_MAC_PERMISSION);
  const mac = macAvailable && macChosen;

  async function handleSubmit(
    text: string,
    model: Model,
    permission: PermissionMode,
    attachments: string[],
    effort: Effort,
  ): Promise<void> {
    if (!user) throw new Error('Профиль недоступен. Повторите отправку.');
    const epoch = getDraftEpoch();
    const session = await createSession.mutateAsync(
      mac ? { model, mac_mode: true, mac_permission: macPermission } : { model },
    );
    if (getDraftEpoch() !== epoch) return;
    setPendingSend(user.id, session.id, text, model, permission, attachments, effort);
    await navigate({ to: '/chat/$sessionId', params: { sessionId: session.id } });
  }

  return (
    <>
      <ChatHeader />
      <EmptyState
        draftScope={chatDraftKey(user?.id ?? -1, 'new')}
        onSubmit={handleSubmit}
        submitting={createSession.isPending}
        mac={mac}
        modeSwitch={
          macAvailable ? (
            <ModeSwitch mac={mac} onChange={setMacChosen} disabled={createSession.isPending} />
          ) : undefined
        }
        toolbarExtra={
          mac ? (
            <MacPermissionPicker
              value={macPermission}
              onChange={setMacPermission}
              disabled={createSession.isPending}
            />
          ) : undefined
        }
        error={
          createSession.error instanceof Error
            ? createSession.error.message
            : createSession.isError
              ? 'Не удалось создать чат'
              : null
        }
      />
    </>
  );
}

/** «Чат | Мак»: a Мак chat also acts on the owner's MacBook (admin only). */
function ModeSwitch({
  mac,
  onChange,
  disabled,
}: {
  mac: boolean;
  onChange: (mac: boolean) => void;
  disabled?: boolean;
}) {
  const option = (value: boolean, label: string, icon: ReactNode) => (
    <button
      type="button"
      aria-pressed={mac === value}
      disabled={disabled}
      onClick={() => onChange(value)}
      className={cn(
        'mc-press inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 text-[14px] disabled:opacity-60',
        mac === value
          ? 'bg-subtle font-medium text-foreground'
          : 'text-muted-foreground md:hover:text-foreground',
      )}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <fieldset
      aria-label="Режим чата"
      className="inline-flex gap-0.5 rounded-full border border-border bg-surface p-0.5"
    >
      {option(false, 'Чат', <MessageSquare size={14} />)}
      {option(true, 'Мак', <Laptop size={14} />)}
    </fieldset>
  );
}
